import { useState } from 'react';
import * as yup from 'yup';
import { Button, Stack, Typography, Box, Divider } from '@mui/material';
import { ControllerRenderProps, FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Sentry from '@sentry/react';
import { createForm, createFormField } from '../../view-models/form.view-model';
import { FDMap } from '../../util/data-structure/FDMap';
import { FormFactoryWithStandardLayout } from '../../components/Form/FormFactory';
import { colorStatusField } from '../../data-fields/CommonFields';
import { FieldFile } from '../../components/Form/Display/FieldFile';
import { RendererType } from '../../data-models/field-definition';
import { IFileMeta } from '../../data-models/field3.data-model';
import { MaggieHttp } from '../../services/MaggieHttp';

export function UI() {
  const testSchema = yup.object().shape({ rubric1: yup.string().required().default('') });
  const colorField = {
    ...colorStatusField({ required: true, key: 'rubric1', label: 'Rubric 01', inlineLabel: true }),
    variant: 'form-inline' as const,
  };
  const form = createForm({ fields: FDMap.fromArray([colorField], 'key') });

  const methods = useForm({
    defaultValues: testSchema.getDefault(),
    resolver: yupResolver(testSchema),
  });

  const [file, setFile] = useState<File | null>(null);
  const handleFileChange = (file: File | null) => {
    setFile(file);
  };

  // Sentry Testing Functions
  const testSentryFunctions = {
    // Console.error tests
    testConsoleError: () => {
      console.error('🔥 Test console.error from UI page', {
        timestamp: new Date().toISOString(),
        page: 'UI',
        testType: 'console_error',
      });
    },

    // Direct Sentry tests
    testSentryException: () => {
      try {
        throw new Error('Test exception from UI page');
      } catch (error) {
        Sentry.captureException(error, {
          tags: { source: 'UI_test', type: 'manual_test' },
          extra: { testFunction: 'testSentryException' },
        });
      }
    },

    testSentryMessage: () => {
      Sentry.captureMessage('Test message from UI page', {
        level: 'error',
        tags: { source: 'UI_test', type: 'manual_test' },
        extra: { testFunction: 'testSentryMessage' },
      });
    },

    // API Error tests (these will be caught by MaggieHttp interceptors)
    testAPI404: async () => {
      try {
        await MaggieHttp.maggie.get('/company/999999');
      } catch (error) {
        console.log('404 error caught (should be in Sentry):', error);
      }
    },

    testAPI500: async () => {
      try {
        await MaggieHttp.maggie.post('/kpiRequest', {
          invalidData: 'this should cause server error',
          companyId: 'not-a-number',
          templateId: null,
        });
      } catch (error) {
        console.log('500 error caught (should be in Sentry):', error);
      }
    },

    testAPI422: async () => {
      try {
        await MaggieHttp.maggie.post('/company', {
          name: '', // empty name should cause validation error
          website: 'invalid-url-format',
        });
      } catch (error) {
        console.log('422 error caught (should be in Sentry):', error);
      }
    },

    testNetworkError: async () => {
      try {
        // This will fail because the endpoint doesn't exist
        await MaggieHttp.maggie.get('/nonexistent-endpoint/test/network/error');
      } catch (error) {
        console.log('Network error caught (should be in Sentry):', error);
      }
    },

    testBulkAPIError: async () => {
      try {
        await MaggieHttp.maggie.put('/kpiRequest/bulkSend', {
          requestIds: [999999, 888888, 777777], // non-existent IDs
        });
      } catch (error) {
        console.log('Bulk API error caught (should be in Sentry):', error);
      }
    },

    // Uncaught error test
    testUncaughtError: () => {
      setTimeout(() => {
        throw new Error('Uncaught error from UI page - should be caught by Sentry automatically');
      }, 100);
    },
  };

  const fileFormField = createFormField<IFileMeta>({
    key: 'file',
    renderer: RendererType.fileUpload,
    disabled: false,
    rendererMeta: {
      acceptTypes: [
        'text/csv',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel',
        'application/pdf',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      ],
      acceptExtensions: ['csv', 'xlsx', 'xls', 'pdf', 'docx', 'pptx', 'doc', 'ppt'],
    },
  });

  return (
    <Stack>
      <Stack flexGrow={'1'} p='1rem'>
        <FieldFile
          formProps={{ onChange: handleFileChange, value: file } as ControllerRenderProps}
          formField={fileFormField}
        />
        <form>
          <FormProvider {...methods}>
            <button
              type='button'
              onClick={() =>
                console.log(methods.watch(), {
                  errors: methods.formState.errors,
                  formState: methods.formState,
                })
              }
            >
              Values & Errors & FormState
            </button>
            <FormFactoryWithStandardLayout form={form} />
            <Button onClick={() => methods.trigger()}>* Submit *</Button>
          </FormProvider>
        </form>
      </Stack>

      {/* Sentry Testing Section */}
      <Box sx={{ p: 3, backgroundColor: '#f5f5f5', margin: '2rem', borderRadius: '8px' }}>
        <Typography variant='h4' sx={{ mb: 2, color: '#d32f2f' }}>
          🧪 Sentry Error Testing
        </Typography>
        <Typography variant='body2' sx={{ mb: 3, color: '#666' }}>
          Use these buttons to test different types of errors in Sentry. Check your Sentry dashboard after
          clicking each button.
        </Typography>

        <Stack spacing={2}>
          {/* Console Error Tests */}
          <Box>
            <Typography variant='h6' sx={{ mb: 1 }}>
              📝 Console Error Tests
            </Typography>
            <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap>
              <Button
                variant='outlined'
                color='warning'
                onClick={testSentryFunctions.testConsoleError}
                size='small'
              >
                Test console.error()
              </Button>
            </Stack>
          </Box>

          <Divider />

          {/* Direct Sentry Tests */}
          <Box>
            <Typography variant='h6' sx={{ mb: 1 }}>
              🎯 Direct Sentry Tests
            </Typography>
            <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap>
              <Button
                variant='outlined'
                color='error'
                onClick={testSentryFunctions.testSentryException}
                size='small'
              >
                Test Sentry Exception
              </Button>
              <Button
                variant='outlined'
                color='info'
                onClick={testSentryFunctions.testSentryMessage}
                size='small'
              >
                Test Sentry Message
              </Button>
              <Button
                variant='outlined'
                color='error'
                onClick={testSentryFunctions.testUncaughtError}
                size='small'
              >
                Test Uncaught Error
              </Button>
            </Stack>
          </Box>

          <Divider />

          {/* API Error Tests */}
          <Box>
            <Typography variant='h6' sx={{ mb: 1 }}>
              🌐 API Error Tests
            </Typography>
            <Typography variant='caption' sx={{ display: 'block', mb: 1, color: '#666' }}>
              These errors will be caught automatically by Axios interceptors
            </Typography>
            <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap>
              <Button variant='contained' color='error' onClick={testSentryFunctions.testAPI404} size='small'>
                Test 404 Error
              </Button>
              <Button variant='contained' color='error' onClick={testSentryFunctions.testAPI500} size='small'>
                Test 500 Error
              </Button>
              <Button
                variant='contained'
                color='warning'
                onClick={testSentryFunctions.testAPI422}
                size='small'
              >
                Test 422 Validation
              </Button>
              <Button
                variant='contained'
                color='secondary'
                onClick={testSentryFunctions.testNetworkError}
                size='small'
              >
                Test Network Error
              </Button>
              <Button
                variant='contained'
                color='primary'
                onClick={testSentryFunctions.testBulkAPIError}
                size='small'
              >
                Test Bulk API Error
              </Button>
            </Stack>
          </Box>

          <Divider />

          {/* Instructions */}
          <Box sx={{ backgroundColor: '#e3f2fd', p: 2, borderRadius: '4px' }}>
            <Typography variant='subtitle2' sx={{ mb: 1 }}>
              📋 Instructions:
            </Typography>
            <Typography variant='body2' sx={{ mb: 1 }}>
              1. Click any button to generate an error
            </Typography>
            <Typography variant='body2' sx={{ mb: 1 }}>
              2. Open DevTools → Console to see local logs
            </Typography>
            <Typography variant='body2' sx={{ mb: 1 }}>
              3. Go to your Sentry dashboard to verify errors are captured
            </Typography>
            <Typography variant='body2'>
              4. Errors should appear with tags like: UI_test, console_error, http_error, etc.
            </Typography>
          </Box>
        </Stack>
      </Box>

      <Stack
        display='grid'
        padding='1rem 2rem'
        gridTemplateColumns='1fr'
        gridTemplateRows={'auto'}
        alignItems={'baseline'}
        alignContent={'start'}
        gap='2rem'
        sx={{ '& .MuiTypography-root': { justifySelf: 'end' } }}
      >
        <Typography variant='h1'>Typography h1</Typography>
        <Typography variant='h2'>Typography h2</Typography>
        <Typography variant='h3'>Typography h3</Typography>
        <Typography variant='h4'>Typography h4</Typography>
        <Typography variant='h5'>Typography h5</Typography>
        <Typography variant='h6'>Typography h6</Typography>
        <Typography variant='subtitle1'>Typography subtitle1</Typography>
        <Typography variant='subtitle2'>Typography subtitle2</Typography>
        <Typography variant='body1'>Typography body1</Typography>
        <Typography variant='body2'>Typography body2</Typography>
        <Typography variant='caption'>Typography caption</Typography>
        <Typography variant='caption2'>New Typography caption2</Typography>
        <Typography variant='overline'>Typography overline</Typography>
        <Typography variant='monotype'>Typography monotype 123</Typography>
      </Stack>
    </Stack>
  );
}
